import React, { useState, useEffect, lazy, Suspense, useCallback, useRef } from 'react';
import './App.css';
import { CircularProgress } from '@mui/material';
import PageLoader from './components/PageLoader';
import AuthLayout from './layouts/AuthLayout';
import UserLayout from './layouts/UserLayout';
import { Routes, Route, useNavigate, useParams, Outlet, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import Navbar from './components/Navbar';
import { LayoutContext } from './Context/LayoutContext';
import { toast, ToastContainer } from 'react-toastify';
import { ThemeColorContextProvider } from './Context/Theme';
import 'react-toastify/dist/ReactToastify.css';
import {
  useGetCartDataAPIQuery,
  useGetNotificationAPIQuery,
  useGetAPIWithoutTokenQuery,
} from './Api/api';
import { READ_ALL_NOTIFICATION, READ_CART_ITEMS } from './Api/apiConstant';
import { setCart, clearCart, setCartId } from './Features/services/cartSlice';
import { setGuestCart } from './Features/services/guestCartSlice';
import { matchCartItemsToBundles } from './global/helpers';
import { useDispatch, useSelector } from 'react-redux';
import TokenExpiryDialog from './components/TokenExpiryDialog';
import ScrollToTop from './ScrollToTop';
import axios from 'axios';
import API_URL from './ServiceModule/Service';
import { removeAllCookies } from './LoginModule/LoginUtility';
import { checkAuth, logoutUser, logout } from './Features/Auth/AuthSlice';
import {
  setCurrentEvent,
  clearCurrentEvent,
  setIsAtAllEvents,
} from './Features/Events/currentEventSlice';
import { onMessage } from 'firebase/messaging';
import { messaging, requestPermission, onMessageListener } from './firebase/firebaseConfig';
import { useSearchParams } from 'react-router-dom';
import { incrementNotificationCount } from '../src/Features/message/messageSlice';
import { skipToken } from '@reduxjs/toolkit/query/react';

// Lazy load route components
const LoginForm = lazy(() => import('./LoginModule/LoginForm'));
const Register = lazy(() => import('./LoginModule/Register'));
const Landing = lazy(() => import('./Landing/Landing'));
const Shop = lazy(() => import('./Shop/Shop'));
const ProductDetail = lazy(() => import('./Shop/ProductDetail'));
const Cart = lazy(() => import('./Cart/Cart'));
const ConfirmOrder = lazy(() => import('./Cart/ConfirmOrder'));
const Profile = lazy(() => import('./Profile/Profile'));
const MyOrder = lazy(() => import('./Profile/MyOrder'));
const ActiveOrders = lazy(() => import('./Profile/ActiveOrders'));
const PurchaseHistory = lazy(() => import('./Profile/PurchaseHistory'));
const MyEvents = lazy(() => import('./Profile/MyEvents'));
const Contact = lazy(() => import('./contact/Contact'));
const ForgotPassword = lazy(() => import('./LoginModule/ForgotPassword'));
const VerifyOTP = lazy(() => import('./LoginModule/VerifyOTP'));
const UpdatePassword = lazy(() => import('./LoginModule/UpdatePassword'));
const DefaultPage = lazy(() => import('./DefaultPage/defaultpage'));
const Search = lazy(() => import('../src/search/search'));
const Notifications = lazy(() => import('./Profile/MyNotifications'));
const Events = lazy(() => import('./AllEvents/AllEventsLanding'));
const TermsAndConditions = lazy(() => import('./Policies/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./Policies/PrivacyPolicy'));
const CancellationPolicy = lazy(() => import('./Policies/CancellationPolicy'));

function ToastNavigationCleaner() {
  const location = useLocation();
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      toast.dismiss();
      prevPath.current = location.pathname;
    }
  }, [location]);

  return null;
}

function App() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const adminPreview = searchParams.get('adminPreview'); // Replace with your query param

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLoggedIn = useSelector((state) => state.auth.isAuthenticated);
  const userId = useSelector((state) => state.auth.user?.user_id);
  const roleId = useSelector((state) => state.auth.user?.role_id);
  const authLoading = useSelector((state) => state.auth.loading);

  const eventName = useSelector((state) => state.currentEvent.eventName);
  const currentEventId = useSelector((state) => state.currentEvent.eventId);
  const eventId = currentEventId;

  const [toastVisible, setToastVisible] = useState(false);

  // Check authentication status on startup
  useEffect(() => {
    dispatch(checkAuth());
  }, [dispatch]);

  const { data: notifcation, refetch } = useGetNotificationAPIQuery(
    isLoggedIn
      ? {
          url: `${READ_ALL_NOTIFICATION}?user_id=${userId}&event_id=${currentEventId || 0}`,
        }
      : skipToken,
    { refetchOnMountOrArgChange: true }
  );

  const cartId = useSelector((state) => state.cart.cartId);
  const eventCartIds = useSelector((state) => state.cart.eventCartIds);

  const handleLogout = useCallback(() => {
    dispatch(logoutUser());
    dispatch(logout());
    dispatch(clearCart());
    removeAllCookies();
    navigate(`/events/${eventName || currentEventId}`);
  }, [currentEventId, eventName, navigate, dispatch]);

  useEffect(() => {
    requestPermission();
  }, []);
  useEffect(() => {
    if (!messaging) return;
    const unsubscribe = onMessage(messaging, (payload) => {
      let data = null;
      if (payload?.data?.notification) {
        try {
          data = JSON.parse(payload.data.notification);
        } catch (e) {}
      }

      // Filter by event_id and user_id to ensure isolation
      const notificationEventId = data?.event_id || payload?.data?.event_id;
      const notificationUserId = data?.user_id || payload?.data?.user_id;

      let shouldShow = true;

      // Event filtering: If notification is targeted at a specific event
      if (notificationEventId && parseInt(notificationEventId, 10) !== 0) {
        if (parseInt(notificationEventId, 10) !== parseInt(currentEventId, 10)) {
          shouldShow = false;
        }
      }

      // User filtering: If notification is targeted at a specific user
      if (notificationUserId && parseInt(notificationUserId, 10) !== 0) {
        if (parseInt(notificationUserId, 10) !== parseInt(userId, 10)) {
          shouldShow = false;
        }
      }

      if (!shouldShow) {
        return; // Suppress notification alert and count increment
      }

      if (data?.order_id) {
        notify(payload.notification, data.order_id);
      } else {
        notify(payload.notification);
      }
      dispatch(incrementNotificationCount());
      if (isLoggedIn) {
        refetch();
      }
    });
    return () => unsubscribe();
  }, [refetch, dispatch, isLoggedIn, currentEventId, userId]);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleMessage = (event) => {
        if (event.data.type === 'INCREMENT_NOTIFICATION_COUNT') {
          if (isLoggedIn) {
            refetch();
          }
        }
      };
      navigator.serviceWorker.addEventListener('message', handleMessage);
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      };
    }
  }, [refetch, isLoggedIn]);

  const handleNotificationclick = useCallback(
    (orderId) => {
      if (orderId) {
        navigate(`/events/${eventName || currentEventId}/active-orders/${orderId}`);
        isLoggedIn && refetch();
      } else {
        navigate(`/events/${eventName || currentEventId}/notification`);
        isLoggedIn && refetch();
      }
    },
    [currentEventId, eventName, navigate, refetch, isLoggedIn]
  );

  const notify = useCallback(
    (notification, orderId) => {
      if (!notification) return;
      const toastId = `notification-${notification.title || 'fcm'}`;

      toast(
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            padding: '6px 4px',
            borderLeft: '4px solid #6264A7', // MS Teams Purple/Indigo Accent
            cursor: 'pointer',
            fontFamily: '"Segoe UI", system-ui, -apple-system, sans-serif',
          }}
          onClick={() => handleNotificationclick(orderId)}
        >
          {notification.image ? (
            <div style={{ marginRight: '12px', flexShrink: 0 }}>
              <img
                src={notification.image}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%', // Circle avatar
                  objectFit: 'cover',
                  border: '1px solid #e2e8f0',
                }}
              />
            </div>
          ) : (
            <div
              style={{
                marginRight: '12px',
                flexShrink: 0,
                width: '36px',
                height: '36px',
                borderRadius: '50%', // Circle fallback
                backgroundColor: '#edebe9', // Teams light gray background
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6264A7',
                fontWeight: '600',
                fontSize: '15px',
              }}
            >
              📢
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
            <div
              style={{
                fontWeight: '600',
                color: '#242424',
                fontSize: '13px',
                lineHeight: '1.3',
                marginBottom: '2px',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}
            >
              {notification.title}
            </div>
            <div
              style={{
                color: '#616161',
                fontSize: '12px',
                lineHeight: '1.4',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}
            >
              {notification.body}
            </div>
          </div>
        </div>,
        { toastId }
      );
    },
    [handleNotificationclick]
  );
  useEffect(() => {
    // Fetch initial CSRF token
    const initCsrf = async () => {
      try {
        await axios.get(`${API_URL}/system/csrf-token`, { withCredentials: true });
      } catch (err) {
        console.error('Failed to initialize CSRF token', err);
      }
    };
    initCsrf();
  }, []);

  useEffect(() => {
    const segments = location.pathname.split('/').filter(Boolean); // Filter empty segments
    const eventsIndex = segments.indexOf('events');

    if (eventsIndex !== -1 && segments[eventsIndex + 1]) {
      const idStr = segments[eventsIndex + 1];
      if (
        [
          'contact',
          'terms-and-conditions',
          'privacy-policy',
          'cancellation-policy',
          'terms&conditions',
        ].includes(idStr)
      ) {
        if (currentEventId !== 0 || eventName !== '') {
          dispatch(setCurrentEvent({ eventName: '', eventId: 0 }));
        }
      } else {
        const parsedId = parseInt(idStr, 10);
        if (!isNaN(parsedId)) {
          if (currentEventId !== parsedId) {
            dispatch(setCurrentEvent({ eventId: parsedId, eventName: '' }));
          }
        } else {
          if (eventName !== idStr) {
            dispatch(setCurrentEvent({ eventId: 0, eventName: idStr }));
          }
        }
      }
    } else {
      if (currentEventId !== 0 || eventName !== '') {
        dispatch(clearCurrentEvent());
      }

      const isAllowedStandalonePath =
        location.pathname.startsWith('/verify') ||
        location.pathname.startsWith('/login') ||
        location.pathname.startsWith('/terms') ||
        location.pathname.startsWith('/privacy') ||
        location.pathname.startsWith('/cancellation-policy') ||
        location.pathname.endsWith('/events') ||
        location.pathname.endsWith('/events/');

      // Only log error and redirect if we are NOT on allowed standalone paths or base /events path
      if (!isAllowedStandalonePath) {
        navigate(`/events/`);
        console.error(
          'No valid segments found in the URL. Path logic triggered for:',
          location.pathname
        );
      }
    }
  }, [location.pathname, navigate, dispatch, currentEventId, eventName]);

  useEffect(() => {
    let logoutTimer;
    if (isLoggedIn) {
      const resetTimer = () => {
        if (logoutTimer) {
          clearTimeout(logoutTimer);
        }
        const includePages = [
          '/cart',
          '/notification',
          '/profile',
          '/confirm-order',
          '/orders',
          '/payment',
        ];

        if (includePages.includes(window.location.pathname) && !adminPreview) {
          logoutTimer = setTimeout(
            () => {
              handleLogout();
            },
            30 * 60 * 1000
          );
        }
      };
      resetTimer();

      window.addEventListener('mousemove', resetTimer);
      window.addEventListener('keydown', resetTimer);

      return () => {
        window.removeEventListener('mousemove', resetTimer);
        window.removeEventListener('keydown', resetTimer);
      };
    } else {
      handleRefresh();
    }
  }, [isLoggedIn, handleLogout, adminPreview]);

  const handleRefresh = useCallback(() => {
    const includePages = [
      '/cart',
      '/notification',
      '/profile',
      '/confirm-order',
      '/orders',
      '/payment',
    ];
    if (
      includePages.includes(window.location.pathname) &&
      !isLoggedIn &&
      roleId != 1 &&
      roleId != 2 &&
      !adminPreview
    ) {
      dispatch(logoutUser());
      dispatch(logout());
      dispatch(clearCart());
      removeAllCookies();
      navigate(`/events/${eventName || currentEventId}`);
    }
  }, [roleId, adminPreview, currentEventId, eventName, navigate, isLoggedIn, dispatch]);

  useEffect(() => {
    let active = true;
    const fetchEventData = async () => {
      let queryParam = '';
      if (currentEventId) {
        queryParam = `event_id=${currentEventId}`;
      } else if (eventName) {
        queryParam = `event_link=${eventName}`;
      }

      if (queryParam) {
        try {
          const response = await axios.get(`${API_URL}/event/getEventNavigation?${queryParam}`, {
            withCredentials: true,
          });
          if (active && response?.data) {
            dispatch(
              setCurrentEvent({
                preorderStartDate: response?.data?.preorder_start_date,
                cityTax: response?.data?.city_tax,
                stateTax: response?.data?.state_tax,
                preorderEndDate: response?.data?.preorder_end_date,
                eventId: response?.data?.event_id,
                eventTaxes: response?.data?.eventTaxes,
                eventNameDisplay: response?.data?.event_name,
                eventName: response?.data?.event_link,
              })
            );
          }
        } catch (error) {
          if (active) {
            const identifier = currentEventId || eventName;
            if (identifier && identifier !== 0) {
              navigate(`/events/${identifier}/NotFound`);
              console.error('Error fetching event navigation:', error);
            }
          }
        }
      }
    };

    fetchEventData();
    return () => {
      active = false;
    };
  }, [currentEventId, eventName, navigate, dispatch]);

  // Dedicated effect to resolve/restore namespaced cartId
  useEffect(() => {
    let active = true;
    const resolveActiveCart = async () => {
      if (userId && userId !== 'null' && userId !== 'undefined' && currentEventId) {
        if (currentEventId in eventCartIds) {
          const cachedCartId = eventCartIds[currentEventId];
          if (active && cartId !== cachedCartId) {
            dispatch(setCartId({ eventId: currentEventId, cartId: cachedCartId }));
          }
        } else {
          try {
            const response = await axios.get(
              `${API_URL}/cart/getActiveCartId?event_id=${currentEventId}&user_id=${userId}`,
              { withCredentials: true }
            );
            const resolvedCartId = response?.data?.data?.cart_id || null;
            if (active) {
              dispatch(setCartId({ eventId: currentEventId, cartId: resolvedCartId }));
            }
          } catch (error) {
            if (active) {
              console.error('Error fetching active cart ID:', error);
            }
          }
        }
      } else {
        if (active && cartId !== null) {
          dispatch(setCartId({ eventId: null, cartId: null }));
        }
      }
    };

    resolveActiveCart();
    return () => {
      active = false;
    };
  }, [userId, currentEventId, eventCartIds, cartId, dispatch]);

  useEffect(() => {
    const eventIdentifier = eventName || currentEventId;
    if (eventIdentifier && !isLoggedIn) {
      const serialized = localStorage.getItem(`guest_cart_items_${eventIdentifier}`);
      let guestItems = [];
      if (serialized) {
        try {
          const parsed = JSON.parse(serialized);
          guestItems = parsed && Array.isArray(parsed.items) ? parsed.items : [];
        } catch (e) {}
      }
      dispatch(setGuestCart(guestItems));
    }
  }, [currentEventId, eventName, isLoggedIn, dispatch]);

  // const shouldSkip = cartId == null || cartId === "" || cartId === "null" || !userId || !eventId;
  const {
    data: cartData,
    isSuccess,
    isError,
    error,
    isLoading,
  } = useGetCartDataAPIQuery(
    {
      url: `${READ_CART_ITEMS}cart_id=${cartId}&user_id=${userId}&event_id=${eventId}`,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !cartId,
    }
  );
  const { data: bundleData } = useGetAPIWithoutTokenQuery(
    currentEventId && currentEventId !== 'null' && currentEventId !== 'undefined'
      ? `/bundle/readAllBundle?event_id=${currentEventId}&type=active`
      : skipToken,
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (isError) {
      if (error?.data?.message === 'Token is not provided') {
        if (eventName !== 'delivery-module' && !adminPreview) {
          handleLogout();
        }
      }
    }
  }, [isError, error]);

  useEffect(() => {
    if (!userId || !cartId) {
      dispatch(
        setCart({
          items: [],
          bundle: [],
          orderSummary: {},
          totalAmount: 0,
          totalQuantity: 0,
        })
      );
    } else if (isSuccess) {
      const { cartItems } = cartData?.data || {};
      const {
        appliedBundles = [],
        remainingItems = [],
        finalCartItems = [],
      } = bundleData?.data
        ? matchCartItemsToBundles(cartItems || [], bundleData.data)
        : { appliedBundles: [], remainingItems: cartItems || [], finalCartItems: cartItems || [] };

      const standaloneQty = remainingItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const bundleQty = appliedBundles.reduce((sum, b) => {
        const itemsInBundle = (b.bundleProduct || b.bundleProducts || []).reduce(
          (acc, bp) => acc + (bp.bundle_quantity || bp.bundleQuantity || bp.quantity || 1),
          0
        );
        return sum + (b.quantity || 0) * (itemsInBundle || 1);
      }, 0);
      const totalQuantity = standaloneQty + bundleQty;

      const cartItem = {
        items: finalCartItems || [],
        bundle: appliedBundles || [],
        orderSummary: cartData?.data?.orderSummary || {},
        totalAmount: 0,
        totalQuantity: totalQuantity || 0,
      };
      dispatch(setCart(cartItem));
    }
  }, [cartData, isSuccess, isError, userId, cartId, dispatch, eventId, navigate, bundleData]);

  if (authLoading) {
    return <PageLoader />;
  }

  return (
    <>
      {/* <TokenExpiryDialog /> */}
      <ToastNavigationCleaner />
      <ThemeColorContextProvider eventId={currentEventId}>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Pages without dynamic top Navbar */}
            <Route element={<AuthLayout />}>
              <Route path="/verify/:token" element={<LoginForm />} />
              <Route path="/verify" element={<LoginForm />} />
              <Route path="/login" element={<LoginForm />} />
              <Route path="/events/:eventId/login" element={<LoginForm />} />
              <Route path="/events/:eventId/verify/:token" element={<LoginForm />} />
              <Route path="/events/:eventId/verify" element={<LoginForm />} />
              <Route path="/events/:eventId/register" element={<Register />} />
              <Route path="/events/:eventId/forgotPassword" element={<ForgotPassword />} />
              <Route path="/events/:eventId/verifyOTP" element={<VerifyOTP />} />
              <Route path="/events/:eventId/UpdatePassword" element={<UpdatePassword />} />
            </Route>

            {/* Pages inside persistent layout wrapper */}
            <Route element={<UserLayout />}>
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/cancellation-policy" element={<CancellationPolicy />} />
              <Route path="/events/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/events/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/events/cancellation-policy" element={<CancellationPolicy />} />
              <Route
                path="/events/:eventId/terms-and-conditions"
                element={<TermsAndConditions />}
              />
              <Route path="/events/:eventId/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/events/:eventId/cancellation-policy" element={<CancellationPolicy />} />
              <Route path="/events" element={<Events />} />
              <Route path="/events/:eventId" element={<EventLandingRoute />} />
              <Route path="/events/:eventId/shop" element={<Shop />} />
              <Route path="/events/:eventId/search" element={<Search />} />
              <Route
                path="/events/:eventId/product-detail/:productId"
                element={<ProductDetail />}
              />
              <Route path="/events/:eventId/cart" element={<Cart />} />
              <Route path="/events/:eventId/confirm-order" element={<ConfirmOrder />} />
              <Route path="/events/:eventId/profile" element={<Profile />} />
              <Route path="/events/:eventId/myEvents" element={<MyEvents />} />
              <Route path="/events/:eventId/active-orders/:id" element={<ActiveOrders />} />
              <Route path="/events/:eventId/purchase-history/:id" element={<PurchaseHistory />} />
              <Route path="/events/:eventId/orders/:id" element={<ActiveOrders />} />
              <Route path="/events/:eventId/notification" element={<Notifications />} />
              <Route path="/events/:eventId/events" element={<MyEvents />} />
              <Route path="/events/:eventId/contact" element={<Contact />} />
              <Route path="/events/contact" element={<Contact />} />
              <Route path="/events/:eventId/NotFound" element={<DefaultPage />} />
              {/* Catch-all 404 route inside layout wrapper */}
              <Route path="*" element={<DefaultPage />} />
            </Route>
          </Routes>
        </Suspense>
      </ThemeColorContextProvider>
      <ToastContainer
        limit={3}
        autoClose={4000}
        closeOnClick={true}
        pauseOnHover={true}
        pauseOnFocusLoss={true}
      />
    </>
  );
}

function EventLandingRoute() {
  const { eventId } = useParams();
  if (eventId === 'contact') return <Contact />;
  if (eventId === 'terms&conditions') return <TermsAndConditions />;
  if (eventId === 'privacy-policy') return <PrivacyPolicy />;
  if (eventId === 'cancellation-policy') return <CancellationPolicy />;
  return <Landing />;
}

export default App;
